<template>
    <base-field 
        :annotation="annotation" 
        :scale="scale"
        :selected="selected"
        :editable="editable"
        @field-click="handleClick"
        @position-change="$emit('position-change', $event)"
    >
        <div class="date-field-display">
            <input
                v-if="editing"
                type="date"
                v-model="localValue"
                :min="annotation.properties.minDate"
                :max="annotation.properties.maxDate"
                @change="handleChange"
                @blur="handleBlur"
                ref="dateInput"
                class="date-input"
            />
            <div v-else class="date-display" :class="{ 'has-value': !!value }">
                <span v-if="formattedValue">{{ formattedValue }}</span>
                <span v-else class="placeholder-text">
                    <i class="fa fa-calendar"></i>
                    {{ annotation.label || annotation.fieldName || 'Select date' }}
                </span>
            </div>
        </div>
    </base-field>
</template>

<script>
import Vue from 'vue';
import BaseField from './BaseField.vue';

export default {
    name: 'DateField',
    components: { BaseField },
    props: {
        annotation: {
            type: Object,
            required: true
        },
        value: {
            type: String,
            default: ''
        },
        scale: {
            type: Number,
            default: 1
        },
        selected: {
            type: Boolean,
            default: false
        },
        editing: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localValue: this.value || ''
        };
    },
    computed: {
        formattedValue() {
            if (!this.value) return '';
            
            try {
                const date = new Date(this.value);
                if (isNaN(date.getTime())) return this.value;
                
                const format = this.annotation.properties.format || 'MM/DD/YYYY';
                
                if (format === 'MM/DD/YYYY') {
                    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
                } else if (format === 'DD/MM/YYYY') {
                    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
                } else if (format === 'YYYY-MM-DD') {
                    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                }
                
                return date.toLocaleDateString();
            } catch (error) {
                return this.value;
            }
        }
    },
    watch: {
        value(newValue) {
            this.localValue = newValue || '';
        },
        editing(isEditing) {
            if (isEditing) {
                this.$nextTick(() => {
                    const input = this.$refs.dateInput;
                    if (input) {
                        input.focus();
                    }
                });
            }
        }
    },
    methods: {
        handleClick() {
            if (!this.editing) {
                this.$emit('field-click', this.annotation);
            }
        },
        
        handleChange() {
            this.$emit('value-change', this.annotation.fieldName, this.localValue);
        },
        
        handleBlur() {
            this.$emit('edit-end', this.annotation);
        }
    }
};
</script>

<style scoped>
.date-field-display {
    width: 100%;
    height: 100%;
}

.date-input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    padding: 4px;
}

.date-display {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    background-color: #fff;
    border: 1px dashed #ccc;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
}

.date-display.has-value {
    border: 1px solid #e2e8f0;
}

.placeholder-text {
    color: #718096;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 8px;
}

.placeholder-text i {
    font-size: 12px;
}
</style>